import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { MessageCircle, Send, Store, Sparkles, ArrowLeft, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { ensureAnonUser } from "@/lib/anonSession";
import { UserTabBar } from "@/pages/UserHubPage";

const fmt = (s) => new Date(s).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });

/** /mi/chat — lista de comercios con los que el usuario puede chatear (Aura o el comercio). */
export default function UserChatListPage() {
  const [chats, setChats] = useState(null);
  useEffect(() => { (async () => { const u = await ensureAnonUser(); const { data } = await api.get(`/pwa/chat/${u.device_id}`); setChats(data.chats); })(); }, []);
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-24" data-testid="user-chat-list">
      <div className="max-w-md mx-auto px-5 pt-8">
        <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-400">Chat a distancia</div>
        <h1 className="font-heading text-3xl font-black tracking-tight leading-none mt-1">MIS <span className="text-emerald-400">CHATS</span></h1>
        <p className="text-sm text-white/60 mt-2">Pregunta lo que quieras a <strong className="text-white">Aura</strong>, la asistente de cada comercio, estés donde estés. Si necesitas a una persona, escribe "hablar con el comercio".</p>
        {!chats ? <div className="mt-8 text-white/60 text-sm">Cargando…</div> : chats.length === 0 ? (
          <div className="mt-8 rounded-3xl bg-white/5 border border-white/10 p-8 text-center" data-testid="user-chat-empty"><MessageCircle className="w-10 h-10 mx-auto text-emerald-400" /><div className="font-heading font-extrabold mt-2">Sin comercios todavía</div><p className="text-sm text-white/60 mt-1">Escanea el QR de cupones o de sorteo de un comercio y aparecerá aquí para chatear.</p></div>
        ) : (
          <div className="mt-6 space-y-2">
            {chats.map((c) => (
              <Link key={c.merchant_id} to={`/mi/chat/${c.merchant_id}`} className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3 hover:bg-white/10" data-testid={`user-chat-${c.merchant_id}`}>
                {c.merchant.logo_url ? <img src={c.merchant.logo_url} alt="" className="w-11 h-11 rounded-xl object-cover" /> : <div className="w-11 h-11 rounded-xl bg-white/10 grid place-items-center"><Store className="w-5 h-5" /></div>}
                <div className="flex-1 min-w-0"><div className="font-bold text-sm">{c.merchant.business_name}</div><div className="text-xs text-white/60 truncate">{c.last ? `${c.last.role === "user" ? "Tú: " : c.last.role === "merchant" ? "Comercio: " : "Aura: "}${c.last.text}` : "Inicia la conversación con Aura ✨"}</div></div>
                {c.unread_user > 0 && <span className="min-w-[20px] h-5 rounded-full bg-[#FF007A] text-[10px] font-black grid place-items-center px-1.5">{c.unread_user}</span>}
              </Link>
            ))}
          </div>
        )}
      </div>
      <UserTabBar active="chat" />
    </div>
  );
}

/** /mi/chat/:merchantId — conversación con Aura / el comercio. Polling cada 5s. */
export function UserChatPage() {
  const { merchantId } = useParams();
  const navigate = useNavigate();
  const [conv, setConv] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [deviceId, setDeviceId] = useState(null);
  const endRef = useRef(null);

  const load = async (id) => { const { data } = await api.get(`/pwa/chat/${id}/${merchantId}`); setConv(data); };
  useEffect(() => {
    let timer;
    (async () => { const u = await ensureAnonUser(); setDeviceId(u.device_id); await load(u.device_id); timer = setInterval(() => load(u.device_id).catch(() => {}), 5000); })();
    return () => clearInterval(timer);
  }, [merchantId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [conv?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim(); if (!t || sending) return;
    setSending(true); setText("");
    setConv((c) => ({ ...c, messages: [...c.messages, { id: "tmp", role: "user", text: t, at: new Date().toISOString() }] }));
    try { await api.post(`/pwa/chat/${merchantId}/send`, { device_id: deviceId, text: t }); await load(deviceId); }
    catch { /* silent */ } finally { setSending(false); }
  };

  if (!conv) return <div className="min-h-screen bg-[#0A0A0A] grid place-items-center text-white/60 text-sm">Abriendo chat…</div>;
  const m = conv.merchant || {};
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col" data-testid="user-chat">
      <div className="sticky top-0 z-10 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/10 px-4 py-3 flex items-center gap-3 max-w-md mx-auto w-full">
        <button onClick={() => navigate("/mi/chat")} className="w-9 h-9 rounded-full bg-white/10 grid place-items-center" data-testid="user-chat-back"><ArrowLeft className="w-4 h-4" /></button>
        {m.logo_url ? <img src={m.logo_url} alt="" className="w-9 h-9 rounded-xl object-cover" /> : <div className="w-9 h-9 rounded-xl bg-white/10 grid place-items-center"><Store className="w-4 h-4" /></div>}
        <div className="flex-1 min-w-0"><div className="font-bold text-sm truncate" data-testid="user-chat-merchant">{m.business_name}</div><div className="text-[10px] text-white/60 flex items-center gap-1">{conv.mode === "merchant" ? <><User className="w-3 h-3" /> Atiende el comercio</> : <><Sparkles className="w-3 h-3 text-[#FF007A]" /> Aura · asistente IA</>}</div></div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4 max-w-md mx-auto w-full space-y-2 pb-28">
        {conv.messages.length === 0 && <div className="rounded-2xl bg-white/5 border border-white/10 p-4 text-sm text-white/70">Hola 👋 Soy Aura, la asistente de <strong className="text-white">{m.business_name}</strong>. Pregúntame por productos, precios, horarios o promociones, aunque estés lejos.</div>}
        {conv.messages.map((msg) => (
          <div key={msg.id + msg.at} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm leading-snug ${msg.role === "user" ? "bg-[#FF007A] text-white rounded-br-md" : msg.role === "merchant" ? "bg-emerald-500 text-black rounded-bl-md" : "bg-white/10 rounded-bl-md"}`} data-testid={`msg-${msg.role}`}>
              {msg.role !== "user" && <div className="text-[9px] uppercase tracking-widest font-black opacity-70 mb-0.5">{msg.role === "merchant" ? m.business_name : "Aura"}</div>}
              {msg.text}<div className="text-[9px] opacity-60 mt-1 text-right">{fmt(msg.at)}</div>
            </div>
          </div>
        ))}
        {sending && <div className="text-xs text-white/50 flex items-center gap-1"><Sparkles className="w-3 h-3 animate-pulse" /> Aura está escribiendo…</div>}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="fixed bottom-0 inset-x-0 bg-[#0A0A0A]/95 backdrop-blur border-t border-white/10 p-3">
        <div className="max-w-md mx-auto flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Escribe tu mensaje…" className="flex-1 h-12 rounded-full bg-white text-black px-4 text-sm outline-none" maxLength={1500} data-testid="user-chat-input" />
          <Button type="submit" disabled={!text.trim() || sending} className="h-12 w-12 rounded-full bg-[#FF007A] hover:bg-[#e6006e] p-0" data-testid="user-chat-send"><Send className="w-4 h-4" /></Button>
        </div>
      </form>
    </div>
  );
}
